// Shared password policy used by client UI and server validation.
// Keep client + server in lockstep so client-side feedback matches what the
// server enforces.

export const PASSWORD_MIN_LENGTH = 8;

// Reasonable special-character set. Include the explicit set the spec calls
// out, plus other common punctuation.
// eslint-disable-next-line no-useless-escape
const SPECIAL_RE = /[!"?=$\/%@#&*()\-_+\[\]{}.,;:<>~`^|\\'']/;
const UPPER_RE = /[A-Z]/;

export type PasswordCheckKey = "length" | "uppercase" | "special";

export interface PasswordCheck {
  key: PasswordCheckKey;
  label: string;
  ok: boolean;
}

export function evaluatePassword(pw: string): PasswordCheck[] {
  return [
    { key: "length", label: `At least ${PASSWORD_MIN_LENGTH} characters`, ok: pw.length >= PASSWORD_MIN_LENGTH },
    { key: "uppercase", label: "At least one uppercase letter", ok: UPPER_RE.test(pw) },
    { key: "special", label: "At least one special character (e.g. ! @ # $ %)", ok: SPECIAL_RE.test(pw) },
  ];
}

export function isPasswordStrong(pw: string): boolean {
  return evaluatePassword(pw).every((c) => c.ok);
}

/** Returns a specific error message, or null when the password passes. */
export function passwordError(pw: string): string | null {
  if (pw.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  if (!UPPER_RE.test(pw)) {
    return "Password must include at least one uppercase letter.";
  }
  if (!SPECIAL_RE.test(pw)) {
    return "Password must include at least one special character.";
  }
  return null;
}
